// src/services/abstract/FetcherService.ts

/**
 * Abstract base class for data fetching services.
 * Provides typed signatures for HTTP-like operations (POST, GET, PATCH, DELETE).
 * All implementations must provide these methods.
 */
export abstract class FetcherService {
  /**
   * Make a POST request to an endpoint.
   * @typeParam T - The response type expected.
   * @param path - The endpoint URL or route.
   * @param body - The request payload as a record of key-value pairs (optional).
   * @returns A promise resolving to the response data.
   */
  abstract post<T = unknown>(
    path: string,
    body?: unknown,
    options?: { headers?: Record<string, string>; onUploadProgress?: (event: ProgressEvent) => void }
  ): Promise<T>;

  /**
   * Make a GET request to an endpoint.
   * @typeParam T - The response type expected.
   * @param path - The endpoint URL or route.
   * @param params - Optional query parameters as a record of key-value pairs.
   * @returns A promise resolving to the response data.
   */
  abstract get<T = unknown>(
    path: string,
    params?: Record<string, string>,
    options?: { headers?: Record<string, string> }
  ): Promise<T>;

  /**
   * Make a PATCH request to an endpoint.
   * @typeParam T - The response type expected.
   * @param path - The endpoint URL or route.
   * @param body - The request payload.
   * @returns A promise resolving to the response data.
   */
  abstract patch<T = unknown>(
    path: string,
    body: unknown,
    options?: { headers?: Record<string, string> }
  ): Promise<T>;

  /**
   * Make a PUT request to an endpoint.
   * @typeParam T - The response type expected.
   * @param path - The endpoint URL or route.
   * @param body - The request payload.
   * @returns A promise resolving to the response data.
   */
  abstract put<T = unknown>(
    path: string,
    body: unknown,
    options?: { headers?: Record<string, string> }
  ): Promise<T>;

  /**
   * Make a DELETE request to an endpoint.
   * @typeParam T - The response type expected.
   * @param path - The endpoint URL or route.
   * @returns A promise resolving to the response data.
   */
  abstract del<T = unknown>(
    path: string,
    body?: unknown,
    options?: { headers?: Record<string, string> }
  ): Promise<T>;
}
